import { getEspaceDistributeurServicesApi, getFileFromResponseOrFail } from '@/core/lib/ApiUtils';
import Cache from '@/core/lib/Cache';
import { DEFAULT_PAGE_SIZE } from '@/core/lib/PaginationUtils';
import { downloadFile } from '@/plugins/documentation/lib/FileDownloadUtils';
import { getFormat } from '@/plugins/documentation/lib/FileFormatUtils';
import { trackEvent } from '@/plugins/gtmTracker/lib/GtmTracker';

// Api types ----------------------------------------------------------------------------------------------------------

interface LibelleApi {
  libelle: string;
}

interface MetadataApi {
  themes: LibelleApi[];
  types: LibelleApi[];
  marches: LibelleApi[];
  offres: LibelleApi[];
}

interface NomApi {
  nom: string;
  nom_sans_extension: string;
}

interface MimeTypeApi {
  mime_type: string | null;
}

interface IsPublicApi {
  is_public: boolean;
}

interface DocumentApi extends NomApi, MimeTypeApi, IsPublicApi {
  id: string;
  date_modification: string;
  description: string | null;
  metadata: MetadataApi | null;
}

interface DocumentToDlApi extends NomApi, MimeTypeApi, IsPublicApi {
  id: string;
  size: number;
}

export interface SearchResultApi {
  total: number;
  page: number;
  documents: DocumentApi[];
}

interface DownloadManyPayloadApi {
  document_ids: string[];
}

// App types ----------------------------------------------------------------------------------------------------------

export interface Filters {
  marches?: string[];
  offres?: string[];
  types?: string[];
  themes?: string[];
}

interface Metadata {
  themes: string[];
  types: string[];
  marches: string[];
  offres: string[];
}

interface Nom {
  nom: string;
  nomSansExtension: string;
}

interface MimeType {
  mimeType?: string;
}

interface Format {
  format?: string;
}

interface IsPublic {
  isPublic: boolean;
}

export interface Document extends Nom, MimeType, Format, IsPublic {
  id: string;
  updatedAt: Date;
  description: string | undefined;
  metadata: Metadata;
}

export interface DocumentToDl extends Nom, MimeType, Format, IsPublic {
  id: string;
  size: number;
}

export interface SearchResult {
  total: number;
  page: number;
  documents: Document[];
}

export enum SortKey {
  NOM = 'nom',
  DATE_MODIFICATION = 'date_modification',
  ACTIONS = 'actions',
}

export interface Sort {
  sortBy: SortKey | undefined;
  desc: boolean | undefined;
}

function mapLibelles(list: LibelleApi[]): string[] {
  return list.map(({ libelle }) => libelle);
}

function mapMetadata(metadataApi: MetadataApi | undefined): Metadata {
  return {
    themes: metadataApi?.themes ? mapLibelles(metadataApi.themes) : [],
    types: metadataApi?.types ? mapLibelles(metadataApi.types) : [],
    marches: metadataApi?.marches ? mapLibelles(metadataApi.marches) : [],
    offres: metadataApi?.offres ? mapLibelles(metadataApi.offres) : [],
  };
}

function mapSearchResult({ documents, page, total }: SearchResultApi): SearchResult {
  return {
    total,
    page,
    documents: documents.map((documentApi: DocumentApi) => ({
      id: documentApi.id,
      nom: documentApi.nom,
      nomSansExtension: documentApi.nom_sans_extension,
      mimeType: documentApi.mime_type ?? undefined,
      format: documentApi.mime_type ? getFormat(documentApi.mime_type) : undefined,
      isPublic: documentApi.is_public,
      updatedAt: new Date(documentApi.date_modification),
      description: documentApi.description ?? undefined,
      metadata: mapMetadata(documentApi.metadata ?? undefined),
    })),
  };
}

function mapDocumentToDownload(documentToDl: DocumentToDlApi): DocumentToDl {
  return {
    id: documentToDl.id,
    nom: documentToDl.nom,
    nomSansExtension: documentToDl.nom_sans_extension,
    mimeType: documentToDl.mime_type ?? undefined,
    format: documentToDl.mime_type ? getFormat(documentToDl.mime_type) : undefined,
    isPublic: documentToDl.is_public,
    size: documentToDl.size,
  };
}

export async function getSearchResult(
  keyword: string | undefined = undefined,
  filters: Filters | undefined = undefined,
  sort: Sort | undefined = undefined,
  page = 0,
): Promise<SearchResult> {
  const queryParams = new URLSearchParams([
    ['page', page.toString()],
    ['page_size', DEFAULT_PAGE_SIZE.toString()],
  ]);

  if (sort) {
    const defaultSortBy = SortKey.NOM;
    const defaultDesc = false;

    const sortBy = sort.sortBy ?? defaultSortBy;
    const desc = sort.desc ?? defaultDesc;

    queryParams.set('sort', sortBy);
    queryParams.set('sort_direction', desc ? 'DESC' : 'ASC');
  }

  if (keyword) {
    queryParams.set('recherche', keyword.trim());
  }

  if (filters) {
    const { marches, offres, types, themes } = filters;

    marches?.forEach(marche => {
      queryParams.append('marches', marche);
    });
    offres?.forEach(offre => {
      queryParams.append('offres', offre);
    });
    types?.forEach(type => {
      queryParams.append('types', type);
    });
    themes?.forEach(theme => {
      queryParams.append('themes', theme);
    });
  }

  const api = await getEspaceDistributeurServicesApi();
  const { data } = await api.get<SearchResultApi>(`/documents?${queryParams.toString()}`);

  return mapSearchResult(data);
}

const cacheReferences = new Cache<Promise<string[]>>();

async function getReferences(name: string): Promise<string[]> {
  return cacheReferences.getOrSet(`references-${name}`, async () => {
    const api = await getEspaceDistributeurServicesApi();
    const { data } = await api.get<LibelleApi[]>(`/documents/${name}`);

    if (data.length === 0) {
      throw new Error(`Aucune référence "${name}" trouvée.`);
    }

    return mapLibelles(data);
  });
}

export async function getMarches(): Promise<string[]> {
  return getReferences('marche');
}

export async function getOffres(): Promise<string[]> {
  return getReferences('offre');
}

export async function getTypes(): Promise<string[]> {
  return getReferences('type');
}

export async function getDocumentToDownload(documentId: string): Promise<DocumentToDl> {
  const api = await getEspaceDistributeurServicesApi(false);
  const { data } = await api.get<DocumentToDlApi>(`/documents/${documentId}/public-info`);

  return mapDocumentToDownload(data);
}

function trackDocumentDownload(id: string, nom: string): void {
  trackEvent({
    event: 'document-download',
    documentId: id,
    documentName: nom,
  });
}

export async function download(id: string, nom: string, isPublic: boolean): Promise<void> {
  const requiresAuth = !isPublic;
  const api = await getEspaceDistributeurServicesApi(requiresAuth);
  const url = isPublic ? `/documents/${id}/public-content` : `/documents/${id}/content`;

  const response = await api.get<ArrayBuffer>(url, { responseType: 'arraybuffer' });

  downloadFile(await getFileFromResponseOrFail(response));
  trackDocumentDownload(id, nom);
}

export async function downloadMany(ids: string[]): Promise<void> {
  const payload: DownloadManyPayloadApi = { document_ids: ids };

  const response = await getEspaceDistributeurServicesApi().then(api =>
    api.post<ArrayBuffer>('/documents/multi-contents', payload, { responseType: 'arraybuffer' }),
  );

  downloadFile(await getFileFromResponseOrFail(response));
}

export async function getDocumentFile({ id, isPublic }: Document): Promise<File> {
  const requiresAuth = !isPublic;
  const api = await getEspaceDistributeurServicesApi(requiresAuth);
  const url = isPublic ? `/documents/${id}/public-content` : `/documents/${id}/content`;

  const response = await api.get<ArrayBuffer>(url, { responseType: 'arraybuffer' });

  return getFileFromResponseOrFail(response);
}
